import { DRIVE_FILE_SCOPE } from '@hive/shared';
import type { Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { connectDrive as saveDriveConnection } from '@/data/vault';
import { queryKeys } from '@/data/keys';
import { desktopBridge } from '@/lib/bridge';
import { supabase } from '@/lib/supabase';
import { AuthContext, RETURN_TO_KEY, type AuthState } from './auth-context';

const GOOGLE_QUERY_PARAMS = { prompt: 'select_account' };

// Connect Google Drive is a second Google sign-in that asks for drive.file and
// offline access. This flag survives the browser redirect, so we know the
// session that comes back carries a refresh token meant for the google function.
const DRIVE_CONNECT_FLAG = 'hive.driveConnect';

function driveFlag(action: 'get' | 'set' | 'clear'): boolean {
  try {
    if (action === 'set') localStorage.setItem(DRIVE_CONNECT_FLAG, String(Date.now()));
    if (action === 'clear') localStorage.removeItem(DRIVE_CONNECT_FLAG);
    const at = Number(localStorage.getItem(DRIVE_CONNECT_FLAG));
    // A connect left unfinished for more than ten minutes is abandoned.
    return at > 0 && Date.now() - at < 10 * 60_000;
  } catch {
    return false;
  }
}

type DriveState = { status: 'idle' | 'connecting' | 'connected' | 'error'; error: string | null };

/** Where a browser sign-in comes back to. Supabase must allow this URL (see the phase 0 brief). */
function browserRedirectUrl(): string {
  return `${window.location.origin}/auth/callback`;
}

/** An error Supabase put in the URL after a failed browser sign-in, if any. */
function errorFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return params.get('error_description') ?? hash.get('error_description') ?? null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(errorFromUrl);
  const [waitingForBrowser, setWaitingForBrowser] = useState(false);
  const [drive, setDrive] = useState<DriveState>(() => ({
    status: driveFlag('get') ? 'connecting' : 'idle',
    error: null,
  }));

  useEffect(() => {
    const client = supabase();
    let active = true;
    void client.auth.getSession().then(({ data, error: sessionError }) => {
      if (!active) return;
      if (sessionError) setError(sessionError.message);
      setSession(data.session);
    });
    const { data } = client.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      if (next) setWaitingForBrowser(false);
      if (next && driveFlag('get')) {
        const refreshToken = next.provider_refresh_token;
        driveFlag('clear');
        if (!refreshToken) {
          setDrive({ status: 'error', error: 'Google didn’t send Drive access. Try Connect Google Drive again.' });
          return;
        }
        // Outside the auth callback: supabase-js warns against awaiting other calls inside it.
        setTimeout(() => {
          saveDriveConnection(refreshToken).then(
            () => {
              setDrive({ status: 'connected', error: null });
              void queryClient.invalidateQueries({ queryKey: queryKeys.googleAccount });
            },
            (e: unknown) =>
              setDrive({ status: 'error', error: e instanceof Error ? e.message : 'Couldn’t connect Google Drive.' }),
          );
        }, 0);
      }
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [queryClient]);

  // Desktop: the system browser finishes Google sign-in and the Electron loopback
  // server hands the code back here, where the PKCE verifier lives.
  useEffect(() => {
    const bridge = desktopBridge();
    if (!bridge) return;
    return bridge.auth.onCallback((result) => {
      setWaitingForBrowser(false);
      if ('error' in result) {
        setError(result.error);
        if (driveFlag('get')) {
          driveFlag('clear');
          setDrive({ status: 'error', error: result.error });
        }
        return;
      }
      void supabase()
        .auth.exchangeCodeForSession(result.code)
        .then(({ error: exchangeError }) => {
          if (exchangeError) setError(exchangeError.message);
        });
    });
  }, []);

  /** Google sign-in through Supabase; desktop goes through the system browser and the loopback server. */
  const startGoogle = useCallback(
    async (options: { scopes?: string; queryParams: Record<string, string> }): Promise<string | null> => {
      const bridge = desktopBridge();
      if (bridge) {
        // Google refuses sign-in inside embedded browsers, so use the system browser.
        const { data, error: oauthError } = await supabase().auth.signInWithOAuth({
          provider: 'google',
          options: { redirectTo: bridge.auth.redirectUrl, skipBrowserRedirect: true, ...options },
        });
        if (oauthError || !data.url) return oauthError?.message ?? 'Could not start Google sign-in.';
        try {
          await bridge.auth.openSignIn(data.url);
          setWaitingForBrowser(true);
        } catch {
          return "Couldn't open your web browser for Google sign-in.";
        }
        return null;
      }
      const { error: oauthError } = await supabase().auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: browserRedirectUrl(), ...options },
      });
      return oauthError?.message ?? null;
    },
    [],
  );

  const signIn = useCallback(async () => {
    setError(null);
    const problem = await startGoogle({ queryParams: GOOGLE_QUERY_PARAMS });
    if (problem) setError(problem);
  }, [startGoogle]);

  const connectDrive = useCallback(async () => {
    setDrive({ status: 'connecting', error: null });
    driveFlag('set');
    try {
      sessionStorage.setItem(RETURN_TO_KEY, '/vault');
    } catch {
      // Lands on the home page instead.
    }
    const email = session?.user.email;
    const problem = await startGoogle({
      scopes: DRIVE_FILE_SCOPE,
      // offline + consent: Google only hands out a refresh token when it asks.
      queryParams: { access_type: 'offline', prompt: 'consent', ...(email ? { login_hint: email } : {}) },
    });
    if (problem) {
      driveFlag('clear');
      setDrive({ status: 'error', error: problem });
    }
  }, [startGoogle, session?.user.email]);

  const signOut = useCallback(async () => {
    const { error: signOutError } = await supabase().auth.signOut();
    if (signOutError) setError(signOutError.message);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      status: session === undefined ? 'loading' : session ? 'signed-in' : 'signed-out',
      session: session ?? null,
      error,
      waitingForBrowser,
      signIn,
      signOut,
      connectDrive,
      drive,
    }),
    [session, error, waitingForBrowser, signIn, signOut, connectDrive, drive],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
